import { api, type NewStructure } from "./api";
import { loadCore } from "./wasm";

// Dropping new builds into the open project: pick files (or a whole kit
// folder) and they arrive in the queue as pending structures.
//
// Two file formats reach us -- WorldEdit/Sponge ".schem" and Minecraft's own
// structure-block ".nbt" -- and BOTH are read by the same C++ loader that the
// generator uses, compiled into the WebAssembly core this page already has
// loaded. Every file is parsed here and handed on as ".schem": a bad file is
// rejected with a reason before it ever reaches the database, the extents come
// along for free (the server has no NBT parser and must not grow one), and the
// project keeps a single stored format.

export interface AddResult {
  added: number;
  // Files that could not be parsed, with the reason -- naming them is the
  // whole point: "3 skipped" leaves the user hunting for which three.
  failed: { name: string; reason: string }[];
}

// Keep a request comfortably under any proxy body limit. base64 inflates by
// 4/3, so this is ~11 MB of structure bytes per POST; kits are thousands of
// small files, and a single huge build still travels in a request of its own.
const BATCH_BYTES = 8 << 20;

export function isStructureFile(name: string): boolean {
  return /\.(schem|nbt)$/i.test(name);
}

function toBase64(bytes: Uint8Array): string {
  // Chunked: String.fromCharCode(...bytes) blows the argument limit on a
  // multi-megabyte build.
  let s = "";
  for (let i = 0; i < bytes.length; i += 0x8000)
    s += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  return btoa(s);
}

// The folder a build came from, used for the queue's folder filter and for
// "apply to folder". A directory pick gives every file its relative path; a
// plain multi-file pick gives none, and the build stands on its own.
function folderOf(f: File): string {
  const rel = (f as File & { webkitRelativePath?: string }).webkitRelativePath;
  if (!rel) return "";
  const parts = rel.split("/");
  parts.pop();
  return parts.join("/");
}

// Parse, convert and upload. `onProgress` is called as files are read so a
// thousand-file kit does not look frozen.
export async function addStructureFiles(
  files: File[],
  onProgress?: (done: number, total: number) => void,
): Promise<AddResult> {
  // A kit folder holds readmes, sidecars and screenshots next to the builds.
  // Those are not failures, they are simply not structures: passing over them
  // silently keeps the report about files that were MEANT to load and did not.
  const wanted = files.filter((f) => isStructureFile(f.name));
  const out: AddResult = { added: 0, failed: [] };
  if (!wanted.length) return out;

  const core = await loadCore();
  let batch: NewStructure[] = [];
  let batchBytes = 0;

  const flush = async () => {
    if (!batch.length) return;
    const res = await api.addStructures(batch);
    out.added += res.added;
    batch = [];
    batchBytes = 0;
  };

  for (let i = 0; i < wanted.length; ++i) {
    const f = wanted[i];
    onProgress?.(i, wanted.length);
    const raw = new Uint8Array(await f.arrayBuffer());
    const info = core.loadSchematic(raw);
    const err = (info as unknown as { error?: string }).error;
    if (err || !info.handle) {
      out.failed.push({ name: f.name, reason: err ?? "nicht lesbar" });
      continue;
    }
    // Always through the writer, never the raw bytes: a ".nbt" becomes the
    // ".schem" the project stores, and a ".schem" comes back normalised the
    // same way a geometry edit would have written it.
    const schem = core.toSchem(info.handle);
    core.freeSchematic(info.handle);
    if (!schem || !schem.length) {
      out.failed.push({ name: f.name, reason: "konnte nicht umgewandelt werden" });
      continue;
    }
    // A copy: the view points into the WASM heap, which moves under us.
    const bytes = new Uint8Array(schem);
    batch.push({
      name: f.name.replace(/\.[^.]+$/, ""),
      folder: folderOf(f),
      source: f.name,
      data: toBase64(bytes),
      sx: info.sx,
      sy: info.sy,
      sz: info.sz,
    });
    batchBytes += bytes.length;
    if (batchBytes >= BATCH_BYTES) await flush();
  }
  await flush();
  onProgress?.(wanted.length, wanted.length);
  return out;
}
